import { CSSProperties } from "react";

import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import Box from "@mui/material/Box";
import type { Theme } from "@mui/material/styles";
import styled from "@mui/material/styles/styled";

import { StepStatus } from "@app/analytics/onboarding/types";

import { Column, StackProps } from "@rui/Stack";

type StepStatusColorMap = {
  [Status in StepStatus]: (theme: Theme) => CSSProperties["borderColor"];
} & { active: (theme: Theme) => CSSProperties["borderColor"] };

const BG_COLOR: StepStatusColorMap = {
  pending: () => "transparent",
  active: () => "transparent",
  complete: (theme) => theme.palette.chart.green.main,
  skipped: () => "#E0E0E0",
};

const BORDER_COLOR: StepStatusColorMap = {
  pending: (theme) => theme.palette.shades.neutral[100],
  active: (theme) => theme.palette.shades.secondary[500],
  complete: (theme) => theme.palette.chart.green.main,
  skipped: (theme) => theme.palette.shades.neutral[100],
};

const STEP_COLOR: StepStatusColorMap = {
  pending: (theme) => theme.palette.overlay.primary[24],
  active: (theme) => theme.palette.shades.secondary[500],
  complete: () => "#fff",
  skipped: (theme) => theme.palette.shades.neutral[500],
};

const ActiveStatusIcon = styled(Box)({
  border: "2px solid",
  borderRadius: "50%",
  width: "70%",
  height: "70%",
});

export const Container = styled(
  ({
    status: _s,
    nested: _n,
    active: _a,
    ...props
  }: {
    status: StepStatus;
    active: boolean;
    nested?: boolean;
  } & StackProps) => <Column variant="center" {...props} />
)(({ status, active, nested, theme }) => ({
  backgroundColor: BG_COLOR[active ? "active" : status](theme),
  flexShrink: 0,
  width: nested ? "16px" : "40px",
  height: nested ? "16px" : "40px",
  borderRadius: nested ? "8px" : "20px",
  border: `${nested ? "1.5px" : "2px"} solid ${BORDER_COLOR[
    active ? "active" : status
  ](theme)}`,
}));

export const StepIcon = ({
  status,
  nested,
  active,
}: {
  status: StepStatus;
  active: boolean;
  nested?: boolean;
}) => {
  const color = STEP_COLOR[active ? "active" : status];

  if (active) {
    return nested ? null : <ActiveStatusIcon sx={{ borderColor: color }} />;
  }

  if (status === "complete") {
    return <CheckIcon sx={{ color, fontSize: nested ? "10px" : "20px" }} />;
  }

  if (status === "skipped") {
    return <CloseIcon sx={{ color, fontSize: nested ? "12px" : "20px" }} />;
  }

  return null;
};
